import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/api"
import ModuleLayout from "@/components/admin/ModuleLayout"
import Badge from "@/components/ui/badge/Badge"
import { toastError, toastSuccess } from "@/lib/toast"
import { getErrorMessage } from "@/lib/errors"
import { LoadState } from "@/components/ui/LoadState"

type SearchResult = { id: string; name: string; email: string; role: string; isActive: boolean }

type AccountDetail = {
  user: {
    id: string
    name: string
    email: string
    phone: string | null
    role: string
    isActive: boolean
    lastLoginAt: string | null
    createdAt: string
  }
  institution: { id: string; name: string } | null
  seedbed: { id: string; name: string } | null
}

export default function SupportAccountsPage() {
  const qc = useQueryClient()
  const [query, setQuery] = useState("")
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [contactName, setContactName] = useState("")
  const [contactEmail, setContactEmail] = useState("")
  const [contactPhone, setContactPhone] = useState("")
  const [unlockReason, setUnlockReason] = useState("")

  const searchQuery = useQuery({
    queryKey: ["support-accounts", "search", query],
    queryFn: async () => (await api.get<{ data: { users: SearchResult[] } }>("/admin/support/users/search", { params: { q: query } })).data.data.users,
    enabled: query.trim().length > 1,
  })

  const detailQuery = useQuery({
    queryKey: ["support-accounts", "detail", selectedId],
    queryFn: async () => {
      const detail = (await api.get<{ data: AccountDetail }>(`/admin/support/users/${selectedId}`)).data.data
      setContactName(detail.user.name)
      setContactEmail(detail.user.email)
      setContactPhone(detail.user.phone ?? "")
      return detail
    },
    enabled: !!selectedId,
  })

  function refreshDetail() {
    qc.invalidateQueries({ queryKey: ["support-accounts", "detail", selectedId] })
  }

  const resetAccessMutation = useMutation({
    mutationFn: async () => api.post(`/admin/support/users/${selectedId}/reset-access`),
    onSuccess: () => toastSuccess("Se envió un correo con la contraseña temporal al usuario."),
    onError: (err) => toastError(err),
  })

  const updateContactMutation = useMutation({
    mutationFn: async () =>
      api.patch(`/admin/support/users/${selectedId}/contact`, { name: contactName, email: contactEmail, phone: contactPhone || null }),
    onSuccess: () => {
      toastSuccess("Datos de contacto actualizados")
      refreshDetail()
    },
    onError: (err) => toastError(err),
  })

  const unlockMutation = useMutation({
    mutationFn: async () => api.post(`/admin/support/users/${selectedId}/unlock`, { reason: unlockReason }),
    onSuccess: () => {
      toastSuccess("Cuenta desbloqueada")
      setUnlockReason("")
      refreshDetail()
    },
    onError: (err) => toastError(err),
  })

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Sistema", "Cuentas de soporte"]}
      title="Cuentas de soporte"
      subtitle="Buscar un usuario y resolver incidencias de acceso, contacto o bloqueo"
      tabs={[]}
      metrics={[]}
    >
      <div className="mb-6">
        <input
          type="text"
          className="h-11 w-full max-w-md rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
          placeholder="Buscar por nombre, email o documento…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {searchQuery.data && searchQuery.data.length > 0 && !selectedId && (
        <ul className="mb-6 divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
          {searchQuery.data.map((u) => (
            <li key={u.id} className="flex items-center justify-between gap-3 p-3 text-sm">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{u.name}</p>
                <p className="text-gray-500">{u.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge color={u.isActive ? "success" : "error"} size="sm">{u.isActive ? "Activo" : "Bloqueado"}</Badge>
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/5"
                  onClick={() => setSelectedId(u.id)}
                >
                  Ver
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {query.trim().length > 1 && searchQuery.data && searchQuery.data.length === 0 && !selectedId && (
        <p className="mb-6 text-sm text-gray-500">Sin resultados.</p>
      )}

      {selectedId && (
        <div className="space-y-6">
          <button
            type="button"
            className="text-sm text-gray-500 hover:underline"
            onClick={() => setSelectedId(null)}
          >
            &larr; Volver a la búsqueda
          </button>

          {detailQuery.isLoading && <LoadState />}
          {detailQuery.isError && <p className="text-sm text-error-600 dark:text-error-400">{getErrorMessage(detailQuery.error)}</p>}
          {detailQuery.data && (
            <>
              <section className="rounded-xl border border-gray-200 p-5 dark:border-gray-800">
                <div className="mb-3 flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{detailQuery.data.user.name}</h3>
                  <Badge color={detailQuery.data.user.isActive ? "success" : "error"} size="sm">
                    {detailQuery.data.user.isActive ? "Activo" : "Bloqueado"}
                  </Badge>
                </div>
                <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                  <div><dt className="text-gray-500">Email</dt><dd className="text-gray-900 dark:text-white">{detailQuery.data.user.email}</dd></div>
                  <div><dt className="text-gray-500">Teléfono</dt><dd className="text-gray-900 dark:text-white">{detailQuery.data.user.phone ?? "—"}</dd></div>
                  <div><dt className="text-gray-500">Institución</dt><dd className="text-gray-900 dark:text-white">{detailQuery.data.institution?.name ?? "—"}</dd></div>
                  <div><dt className="text-gray-500">Semillero</dt><dd className="text-gray-900 dark:text-white">{detailQuery.data.seedbed?.name ?? "—"}</dd></div>
                  <div><dt className="text-gray-500">Último acceso</dt><dd className="text-gray-900 dark:text-white">{detailQuery.data.user.lastLoginAt ? new Date(detailQuery.data.user.lastLoginAt).toLocaleString("es-CO") : "Sin registro"}</dd></div>
                </dl>
              </section>

              <section className="rounded-xl border border-gray-200 p-5 dark:border-gray-800">
                <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Restablecer acceso</h3>
                <p className="mb-3 text-sm text-gray-500">Genera una contraseña temporal y la envía por correo al usuario. Nunca se muestra aquí.</p>
                <button
                  type="button"
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                  disabled={resetAccessMutation.isPending}
                  onClick={() => resetAccessMutation.mutate()}
                >
                  {resetAccessMutation.isPending ? "Enviando…" : "Enviar contraseña temporal por correo"}
                </button>
              </section>

              <section className="rounded-xl border border-gray-200 p-5 dark:border-gray-800">
                <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Corregir datos de contacto</h3>
                <div className="mb-3 grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-500">Nombre</label>
                    <input className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white" value={contactName} onChange={(e) => setContactName(e.target.value)} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-500">Email</label>
                    <input type="email" className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-500">Teléfono</label>
                    <input className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                  disabled={updateContactMutation.isPending}
                  onClick={() => updateContactMutation.mutate()}
                >
                  {updateContactMutation.isPending ? "Guardando…" : "Guardar cambios"}
                </button>
              </section>

              {!detailQuery.data.user.isActive && (
                <section className="rounded-xl border border-gray-200 p-5 dark:border-gray-800">
                  <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Desbloquear cuenta</h3>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Motivo (obligatorio)</label>
                  <textarea
                    className="mb-3 w-full rounded-lg border border-gray-300 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                    rows={2}
                    value={unlockReason}
                    onChange={(e) => setUnlockReason(e.target.value)}
                    placeholder="Ej: usuario confirmó identidad por WhatsApp"
                  />
                  <button
                    type="button"
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                    disabled={unlockMutation.isPending || unlockReason.trim().length < 3}
                    onClick={() => unlockMutation.mutate()}
                  >
                    {unlockMutation.isPending ? "Desbloqueando…" : "Desbloquear cuenta"}
                  </button>
                </section>
              )}
            </>
          )}
        </div>
      )}
    </ModuleLayout>
  )
}
